import * as React from "react";
import { JSONUIProvider, Renderer } from "@json-render/react";
import type { StateStore } from "@json-render/react";
import type { AppViewSpec } from "../catalog";
import { compileAppThemeCss, gentlyTheme } from "../theme";
import type { AppTheme } from "../theme";
import { appComponents } from "./registry";
import { AppNavigateContext } from "./links";
import type { AppNavigate } from "./links";
import {
  AppScopeContext,
  AppTableActionsContext,
  AppThemeCssContext,
} from "./scope";
import type { AppExecutionDiagnostics } from "./diagnostics";

const InspectedAppView = import.meta.env.DEV
  ? React.lazy(() => import("./inspector"))
  : null;

/**
 * Browser entry point for Apps (`@erp/json-renderer/apps/react`).
 *
 * Pulls in `@erp/ui`, so it must never be imported from server code; the
 * backend uses the server-safe `@erp/json-renderer/apps` entry.
 */

export type AppActionHandler = (
  params: Record<string, unknown>,
) => Promise<unknown> | unknown;

export interface AppViewProps {
  /** The view to render. `null` renders nothing (e.g. while the App loads). */
  spec: AppViewSpec | null;
  /** Initial state that `{ $state }` and `{ $bindState }` expressions read. */
  state?: Record<string, unknown>;
  /**
   * An external state store (from `createAppStateStore`), for hosts that write
   * state after mount, e.g. feeds a command refreshed. Takes precedence over
   * `state` and `onStateChange`.
   */
  store?: StateStore;
  /** Action handlers keyed by action name, called when a bound event fires. */
  handlers?: Record<string, AppActionHandler>;
  /** Called whenever bound state changes (an input edited, a row pressed). */
  onStateChange?: (changes: Array<{ path: string; value: unknown }>) => void;
  /** The theme. Defaults to Gently. */
  theme?: AppTheme;
  /** Whether the spec is still streaming in. */
  loading?: boolean;
  /**
   * Navigates the host app for in-app links (`/invoices/42`), usually its
   * router's `navigate`. Without it, in-app links are plain anchors that
   * reload the page.
   */
  navigate?: AppNavigate;
  /** Controls supplied by the host for the table's visible columns and rows. */
  renderTableActions?: (columns: string[], rows: string[][]) => React.ReactNode;
  /**
   * Optional causal trace from the host that owns this view. The inspector
   * shows it in an Execution tab; a host without a controller keeps the
   * generic handler log.
   */
  diagnostics?: AppExecutionDiagnostics;
}

export type { AppNavigate };
export type {
  AppExecutionDiagnostics,
  AppExecutionSnapshot,
  AppExecutionTraceEntry,
  AppTraceOutcome,
} from "./diagnostics";
export { executionTab } from "./diagnostics";

/**
 * Render an App view with the Gently component set, scoped to its theme.
 *
 * @example
 * ```tsx
 * <AppView spec={spec} state={{ rows }} handlers={{ approve }} />
 * ```
 */
export function AppView(props: AppViewProps): React.JSX.Element {
  if (
    InspectedAppView &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("inspectApps") === "1"
  ) {
    return (
      <React.Suspense fallback={null}>
        <InspectedAppView {...props} content={AppViewContent} />
      </React.Suspense>
    );
  }
  return <AppViewContent {...props} />;
}

function AppViewContent({
  spec,
  state,
  store,
  handlers,
  onStateChange,
  theme = gentlyTheme,
  loading,
  navigate,
  renderTableActions,
  inspection,
}: AppViewProps & { inspection?: React.ReactNode }): React.JSX.Element {
  // One scope per view: the theme id is metadata, not a selector.
  const scope = `app${React.useId().replace(/[^\w-]/g, "")}`;
  const css = compileAppThemeCss(theme, scope);
  return (
    <div
      data-app-theme={theme.id}
      data-app-scope={scope}
      className="min-w-0 text-sm text-foreground"
    >
      {css && <style>{css}</style>}
      <AppScopeContext value={scope}>
        <AppThemeCssContext value={css}>
          <AppTableActionsContext value={renderTableActions}>
            <AppNavigateContext value={navigate}>
              <JSONUIProvider
                registry={appComponents}
                store={store}
                initialState={state ?? spec?.state}
                handlers={handlers}
                onStateChange={onStateChange}
              >
                {inspection}
                <Renderer
                  spec={spec}
                  registry={appComponents}
                  loading={loading}
                />
              </JSONUIProvider>
            </AppNavigateContext>
          </AppTableActionsContext>
        </AppThemeCssContext>
      </AppScopeContext>
    </div>
  );
}

export type { StateStore as AppStateStore } from "@json-render/react";
export { createStateStore as createAppStateStore } from "@json-render/react";
